
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import Video from 'plaid-threads/Icons/Video';
export const metadata = {
  toc: true,
  layout: 'guide',
  description: 'Discover best practices for improving Link conversion'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Callout = makeShortcode("Callout");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <Header title='Optimizing Link conversion' subtitle='Discover best practices for improving Link conversion' mdxType="Header" />
    <h4 {...{
      "id": "overview"
    }}>{`Overview`}</h4>
    <p>{`This guide contains tips for optimizing your existing Link implementation to help increase conversion and improve your users' experiences with Link. If you don't yet have a working Link integration, see the Link page for your desired SDK for instructions on getting started with Link.`}</p>
    <Callout mdxType="Callout">
      <p>{`If you are using pay-by-bank flows, also see `}<a parentName="p" {...{
          "href": "/docs/auth/pay-by-bank-ux"
        }}>{`Increasing pay-by-bank adoption`}</a>{` for tips on getting users to use the Link-based payments flow rather than paying via card.`}</p>
    </Callout>
    <h4 {...{
      "id": "measuring-link-conversion"
    }}>{`Measuring Link conversion`}</h4>
    <p>{`Before making changes to your integration to improve conversion, it can be useful to understand your current conversion rates. To learn how, see `}<a parentName="p" {...{
        "href": "/docs/link/measuring-conversion"
      }}>{`Tracking Link conversion`}</a>{`.`}</p>
    <h4 {...{
      "id": "improving-link-conversion"
    }}>{`Improving Link conversion`}</h4>
    <p>{`Many different steps can be taken to maximize Link conversion, and the exact impact of these steps will vary for each customer and use case. The recommendations below are provided in general priority order.`}</p>
    <h5 {...{
      "id": "initialize-with-a-minimal-set-of-products"
    }}>{`Initialize with a minimal set of products`}</h5>
    <p>{`In general, calling `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` with a minimal set of products will both increase conversion and reduce your costs. For more details, see `}<a parentName="p" {...{
        "href": "/docs/link/initializing-products"
      }}>{`Choosing how to initialize products`}</a>{`.`}</p>
    <h5 {...{
      "id": "implement-full-oauth-support-on-mobile-including-app-to-app"
    }}>{`Implement full OAuth support on mobile, including app-to-app`}</h5>
    <p>{`While supporting mobile app-to-app impacts only a small number of financial institutions (currently Chase and Chime), the impact on conversion for eligible users is very large, as app-to-app flows can allow users to authenticate with biometrics instead of a username and password. For iOS and Flutter on iOS, supporting app-to-app requires `}<a parentName="p" {...{
        "href": "/docs/link/oauth/#ios-sdk-react-native-ios-flutter-ios"
      }}>{`creating an `}<inlineCode parentName="a">{`apple-app-site-association`}</inlineCode>{` file`}</a>{` to support universal links. On Android and Flutter on Android, supporting app-to-app requires `}<a parentName="p" {...{
        "href": "/docs/link/oauth/#android-sdk-react-native-android-flutter-android"
      }}>{`registering your Android package name`}</a>{`. For more details, see the `}<a parentName="p" {...{
        "href": "/docs/link/oauth"
      }}>{`OAuth Guide`}</a>{`.`}</p>
    <h5 {...{
      "id": "pre-initialize-link-for-lower-ui-latency"
    }}>{`Pre-initialize Link for lower UI latency`}</h5>
    <p>{`All Plaid SDKs offer a method to pre-initialize Link before opening it -- for example, by calling `}<inlineCode parentName="p">{`create()`}</inlineCode>{` on web, `}<inlineCode parentName="p">{`createPlaidLinkSession()`}</inlineCode>{` on React Native, or creating a `}<inlineCode parentName="p">{`PlaidLinkSession`}</inlineCode>{` on iOS, Android, or Flutter. It is recommended to call this method as soon as you load the view or screen from which the end user can open Link. Pre-initializing Link as soon as possible, before the `}<inlineCode parentName="p">{`open()`}</inlineCode>{` method is called, results in lower latency for your end-user in loading the Link UI, leading to increased conversion.`}</p>
    <p>{`Pre-initialization is supported in recent Plaid Link SDK versions. You must be using an up-to-date version of Plaid Link SDKs to benefit from the performance enhancements of this approach.`}</p>
    <h5 {...{
      "id": "provide-pre-link-messaging"
    }}>{`Provide pre-Link messaging`}</h5>
    <p>{`Link conversion is highest when users have the right expectations set going into Link. Your app should explain why you use Plaid, the benefits for the user of connecting their account, and that the user's information will be secure. It should also set the user's expectations around what information they will need to provide during the Link flow. Plaid should be configured as the default experience. For more details, including visual examples, see `}<a parentName="p" {...{
        "href": "/docs/link/messaging"
      }}>{`Pre-Link messaging for optimizing conversion`}</a>{`.`}</p>
    <h5 {...{
      "id": "update-the-link-sdk-regularly"
    }}>{`Update the Link SDK regularly`}</h5>
    <p>{`Plaid makes continual updates to Plaid Link to improve conversion. If you are using Link on web, you will automatically be on the latest version; if you are using one of Plaid's SDKs for `}<a parentName="p" {...{
        "href": "https://github.com/plaid/plaid-link-ios"
      }}>{`iOS`}</a>{`, `}<a parentName="p" {...{
        "href": "https://github.com/plaid/plaid-link-android"
      }}>{`Android`}</a>{`, `}<a parentName="p" {...{
        "href": "https://github.com/plaid/react-native-plaid-link-sdk"
      }}>{`React Native`}</a>{`, or `}<a parentName="p" {...{
        "href": "https://github.com/plaid/plaid-link-flutter"
      }}>{`Flutter`}</a>{`, we recommend you update regularly (ideally once a month, and at least once per quarter) to ensure you have access to the latest Link conversion improvements.`}</p>
    <h5 {...{
      "id": "provide-a-phone-number-during-link-token-creation"
    }}>{`Provide a phone number during Link token creation`}</h5>
    <p>{`If you have the user's phone number, provide it in the `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` request in the `}<inlineCode parentName="p">{`user.phone_number`}</inlineCode>{` field. Plaid will pre-fill the phone number in Link for the user, allowing them to access the Returning User Experience without any data entry. For more details, see `}<a parentName="p" {...{
        "href": "/docs/link/returning-user"
      }}>{`Returning user experience`}</a>{`.`}</p>
    <h5 {...{
      "id": "for-auth-customers-implement-alternative-auth-flows"
    }}>{`(For Auth customers) Implement alternative Auth flows`}</h5>
    <p>{`While the vast majority of users can use Plaid's default Auth flows, some users, especially those at smaller banks and credit unions, have accounts that do not support those flows. Implementing micro-deposit-based or Database Auth flows will increase conversion by allowing these users to link their accounts. For more details, see `}<a parentName="p" {...{
        "href": "/docs/auth/coverage"
      }}>{`Auth coverage`}</a>{`.`}</p>
    <h5 {...{
      "id": "to-increase-plaid-uptake-implement-embedded-institution-search"
    }}>{`(To increase Plaid uptake) Implement Embedded Institution Search`}</h5>
    <p>{`Implementing the `}<a parentName="p" {...{
        "href": "/docs/link/embedded-institution-search/"
      }}>{`Embedded Institution Search`}</a>{` UI for Link has been shown to increase the percentage of customers who choose Plaid over an alternative flow. This is especially useful for payments (Auth and Transfer) use cases.`}</p>
    <h5 {...{
      "id": "for-aggregation-use-cases-use-multi-item-link"
    }}>{`(For aggregation use cases) Use Multi-Item Link`}</h5>
    <p>{`While `}<a parentName="p" {...{
        "href": "/docs/link/multi-item-link"
      }}>{`Multi-Item Link`}</a>{` doesn't directly increase the conversion of a Link session, it does increase the number of Items added per user by reducing the friction of adding multiple Items. This achieves a similar outcome to improved Link conversion for use cases where users are expected to add Items at multiple institutions.`}</p>
    <h5 {...{
      "id": "configure-link-for-your-users-country-and-language"
    }}>{`Configure Link for your user's country and language`}</h5>
    <p>{`For apps with multi-language experiences, `}<a parentName="p" {...{
        "href": "/docs/link/customization/"
      }}>{`custom profiles`}</a>{` improve conversion by allowing you to display Link in your user's preferred language. Calling `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` with the `}<inlineCode parentName="p">{`country_codes`}</inlineCode>{` parameter set to your user's specific country, rather than all countries your app supports, will allow Link to show a more accurately targeted list of institutions. For Auth customers, calling `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` with `}<inlineCode parentName="p">{`country_codes`}</inlineCode>{` set to just `}<inlineCode parentName="p">{`US`}</inlineCode>{` is also required to enable the conversion-maximizing Instant Match and micro-deposit based `}<a parentName="p" {...{
        "href": "/docs/auth/coverage"
      }}>{`Auth flows`}</a>{`.`}</p>
    <h5 {...{
      "id": "customize-link-with-your-organizations-branding"
    }}>{`Customize Link with your organization's branding`}</h5>
    <p>{`Plaid allows you to customize certain aspects of the Link UI. Customizing these in a way that matches your app -- for example, `}<a parentName="p" {...{
        "href": "/docs/link/customization/#consent-pane-customizations"
      }}>{`uploading your organization's logo`}</a>{` to be used on the Link consent pane or `}<a parentName="p" {...{
        "href": "/docs/link/customization/#color-scheme"
      }}>{`matching your brand colors`}</a>{` can increase conversion. For more details, see `}<a parentName="p" {...{
        "href": "/docs/link/customization/"
      }}>{`Customizing Link`}</a>{`.`}</p>
    <h5 {...{
      "id": "if-applicable-use-the-institution-select-shortcut"
    }}>{`(If applicable) Use the Institution Select shortcut`}</h5>
    <p>{`If you already know the institution your user plans to connect before the Link flow is launched, you can highlight this institution in the Institution Select UI by specifying the `}<a parentName="p" {...{
        "href": "/docs/api/link/#link-token-create-request-institution-data-routing-number"
      }}>{`routing number`}</a>{` during the `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` call. For more details, see `}<a parentName="p" {...{
        "href": "/docs/link/customization/#institution-select-shortcut"
      }}>{`Institution Select shortcut`}</a>{`.`}</p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "overview",
        level: 4,
        title: "Overview",
      },
{
        id: "measuring-link-conversion",
        level: 4,
        title: "Measuring Link conversion",
      },
{
        id: "improving-link-conversion",
        level: 4,
        title: "Improving Link conversion",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
